import { useMemo, useState } from "react";
import { AppStoreButton } from "@/components/AppStoreButton";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export const SafeSpendSimulator = () => {
  const [balance, setBalance] = useState(2400);
  const [bills, setBills] = useState(1750);
  const [bufferDays, setBufferDays] = useState(3);

  const safe = useMemo(() => {
    const dailyBurn = bills / 30;
    return Math.max(0, balance - bills - dailyBurn * bufferDays);
  }, [balance, bills, bufferDays]);

  return (
    <section
      id="simulator"
      data-testid="simulator-section"
      className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8"
    >
      <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-[#0D1527] to-[#070C18] p-8 text-center sm:p-12">
        <p className="font-mono-num text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
          Try it before you download
        </p>
        <h2 className="mx-auto mt-3 max-w-xl text-2xl font-bold tracking-tight text-white sm:text-3xl">
          What's your Pulse Number right now?
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm text-slate-400">
          Drag the sliders. This is a taste of what Payment Pulse does live,
          every single day, with your real accounts.
        </p>

        <div className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-8 text-left sm:grid-cols-2">
          <div className="space-y-7">
            <div>
              <div className="mb-3 flex items-center justify-between">
                <label htmlFor="sim-balance" className="text-sm text-slate-300">
                  Current balance
                </label>
                <span className="font-mono-num text-sm font-semibold text-white">
                  {fmt(balance)}
                </span>
              </div>
              <input
                id="sim-balance"
                data-testid="simulator-balance-slider"
                type="range"
                min="100"
                max="10000"
                step="50"
                value={balance}
                onChange={(e) => setBalance(Number(e.target.value))}
                className="pulse-slider"
              />
            </div>
            <div>
              <div className="mb-3 flex items-center justify-between">
                <label htmlFor="sim-bills" className="text-sm text-slate-300">
                  Upcoming bills (next 30 days)
                </label>
                <span className="font-mono-num text-sm font-semibold text-white">
                  {fmt(bills)}
                </span>
              </div>
              <input
                id="sim-bills"
                data-testid="simulator-bills-slider"
                type="range"
                min="0"
                max="8000"
                step="50"
                value={bills}
                onChange={(e) => setBills(Number(e.target.value))}
                className="pulse-slider"
              />
            </div>
            <div>
              <div className="mb-3 flex items-center justify-between">
                <label htmlFor="sim-buffer" className="text-sm text-slate-300">
                  Safety buffer
                </label>
                <span className="font-mono-num text-sm font-semibold text-white">
                  {bufferDays} days
                </span>
              </div>
              <input
                id="sim-buffer"
                data-testid="simulator-buffer-slider"
                type="range"
                min="1"
                max="7"
                step="1"
                value={bufferDays}
                onChange={(e) => setBufferDays(Number(e.target.value))}
                className="pulse-slider"
              />
            </div>
          </div>

          <div className="flex flex-col items-center justify-center rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-8">
            <p className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Safe to spend today
            </p>
            <p
              data-testid="simulator-result-safe-spend"
              className="font-mono-num mt-2 text-5xl font-semibold text-emerald-400"
            >
              {fmt(safe)}
            </p>
            <p className="mt-3 text-xs leading-relaxed text-slate-400">
              {bills > balance
                ? "Your bills exceed your balance — Payment Pulse helps you plan which to pay first."
                : "Spend up to this today and every bill still clears on time."}
            </p>
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <AppStoreButton testId="simulator-appstore-button" sublabel="Get the real thing" />
        </div>
      </div>
    </section>
  );
};
